import React, { useEffect, useState } from "react";
import { Grid, Box, Button } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import {
  getPlaylists,
  createPlaylist,
  createLibraryPlaylist,
  editPlaylist,
  deletePlaylist,
  downloadVibyJingle,
} from "../api";
import PlaylistModal from "../components/PlaylistModal";
import DeleteModal from "../components/DeleteModal";
import Loader from "../components/common/Loader";
import PlaylistPlaceholder from "../components/PlaylistPlaceholder";
import PlaylistCard from "../components/PlaylistCard";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";

export default function PlaylistsPage() {
  const { eventId } = useParams();
  const location = useLocation();
  const eventNameFromNavigation = location.state?.eventName;
  const [state, setState] = useState({
    playlists: [],
    isLoading: true,
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setState((s) => ({
        ...s,
        role: user.role,
      }));
    }
  }, []);

  const handleCreate = (index, libraryTemplate) => {
    setState((s) => ({
      ...s,
      open: true,
      playlist: libraryTemplate,
      playlistIndex: index,
      modalTitle: `Create Playlist for ${
        eventNameFromNavigation || "this event"
      }`,
      handleConfirm: async (body) => {
        const newPlaylist = await createPlaylist(body);
        if (body.addToLibrary) {
          const fallbackName = `Event playlist ${new Date().toLocaleString()}`;
          await createLibraryPlaylist({
            name: body.name || fallbackName,
            playlist: body.playlist,
          });
        }

        setState((s) => ({
          ...s,
          open: false,
          playlists: [...s.playlists, newPlaylist]
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .map((playlist, index) => ({ ...playlist, index })),
        }));
      },
    }));
  };

  const handleEdit = (item) => () => {
    setState((s) => ({
      ...s,
      open: true,
      playlist: item,
      modalTitle: "Edit Playlist",
      eventDate: s.eventDate,
      venueId: s.venueId,
      venueCurrency: s.venueCurrency,
      handleConfirm: async (body) => {
        try {
          const data = await editPlaylist(body);
          // If the API call fails, `data` might be an error object without an id.
          // This check prevents state corruption.
          
          if (!data || !data.id) {
            console.error("Failed to edit playlist. API response:", data);
            return "Failed to edit playlist. Please try again.";
          }

          const updatedPlaylists = s.playlists
            .map((p) =>
              p.id === item.id
                ? {
                    ...p,
                    ...data,
                    playlist: data.playlist || body.playlist || [],
                    startTime: data.startTime || body.startTime,
                    endTime: data.endTime || body.endTime,
                  }
                : p
            )
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .map((playlist, index) => ({ ...playlist, index }));

          setState((s) => ({
            ...s,
            open: false,
            playlists: updatedPlaylists,
          }));
        } catch (error) {
          console.error("An error occurred while editing the playlist:", error);
          return "An error occurred while editing the playlist. Please try again.";
        }
      },
    }));
  };

  const handleDownload = (item) => () => {
    const element = document.createElement("a");
    const file = new Blob([item.playlist.map((x) => x.item).join("\n")], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "playlist.txt";
    document.body.appendChild(element);
    element.click();
  };

  const handleVibyJingleDownload = async () => {
    try {
      const response = await downloadVibyJingle();

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      
      const element = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      element.href = url;
      element.download = "viby-jingle.wav";
      element.style.display = "none";
      
      document.body.appendChild(element);
      element.click();
      
      document.body.removeChild(element);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Viby jingle:', error);
      alert('Failed to download the Viby jingle. Please try again.');
    }
  };

  const handleClose = () => {
    setState((s) => ({
      ...s,
      open: false,
      playlist: undefined,
      handleConfirm: undefined,
      modalTitle: undefined,
    }));
  };

  const handleDelete = (item) => async () => {
    setState((s) => ({
      ...s,
      delete: true,
      deleteName: item.name,
      handleDeleteConfirm: async () => {
        await deletePlaylist(item);
        setState((s) => ({
          ...s,
          delete: false,
          deleteName: undefined,
          handleDeleteConfirm: undefined,
          playlists: s.playlists
            .filter((x) => x.id !== item.id)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .map((playlist, index) => ({ ...playlist, index })),
        }));
      },
    }));
  };

  const handleDeleteClose = () => {
    setState((s) => ({
      ...s,
      delete: false,
      deleteName: undefined,
      handleDeleteConfirm: undefined,
    }));
  };

  useEffect(() => {
    (async () => {
      const playlistData = await getPlaylists(eventId);
      setState((s) => ({
        ...s,
        playlists: (playlistData.playlists || [])
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
          .map((playlist, index) => ({
            ...playlist,
            index,
          })),
        eventDate: playlistData.eventDate,
        venueId: playlistData.venueId,
        venueCurrency: playlistData.venueCurrency,
        isLoading: false,
      }));
    })();
  }, [eventId]);

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 4,
        maxWidth: "1600px",
        margin: "0 auto",
        paddingTop: "80px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          position: "relative",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleVibyJingleDownload}
            sx={{
              backgroundColor: "#cf1b88",
              color: "white",
              borderRadius: "12px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "600",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(207, 27, 136, 0.3)",
              "&:hover": {
                backgroundColor: "#a51668",
                boxShadow: "0 6px 16px rgba(207, 27, 136, 0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Viby Jingle
          </Button>
        </Box>

        <Grid
          container
          spacing={4}
          sx={{
            width: "100%",
            margin: "0 auto",
            justifyContent: "center",
            maxWidth: 900,
          }}
        >
          {state.playlists.map((playlist, index) => (
            <Grid
              item
              xs={12}
              sm="auto"
              key={playlist.id || index}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box sx={{ width: 400, maxWidth: 400, minWidth: 400 }}>
                <PlaylistCard
                  playlist={playlist}
                  role={state.role}
                  onEdit={handleEdit(playlist)}
                  onDelete={handleDelete(playlist)}
                  onDownload={handleDownload(playlist)}
                />
              </Box>
            </Grid>
          ))}
          {Array.from({ length: Math.max(2 - state.playlists.length, 0) }).map(
            (_, i) => (
              <Grid
                item
                xs={12}
                sm="auto"
                key={`placeholder-${i}`}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: 400, maxWidth: 400, minWidth: 400 }}>
                  <PlaylistPlaceholder
                    index={state.playlists.length + i}
                    totalCount={
                      state.playlists.length +
                      Math.max(2 - state.playlists.length, 0)
                    }
                    onCreateClick={() => handleCreate(state.playlists.length + i)}
                  />
                </Box>
              </Grid>
            )
          )}
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            key="add-playlist-btn"
          >
            <Button
              variant="outlined"
              sx={{
                width: 200,
                height: 200,
                border: "2px solid #a084e8",
                color: "#a084e8",
                borderRadius: "24px",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 64,
                minWidth: 0,
                p: 0,
                "&:hover": {
                  background: "#f3f0fa",
                  borderColor: "#7c5fe6",
                  color: "#7c5fe6",
                },
              }}
              onClick={() => handleCreate(state.playlists.length)}
              disabled={state.playlists.length < 2}
            >
              <AddIcon sx={{ fontSize: 64 }} />
            </Button>
          </Grid>
        </Grid>
      </Box>

      {state.open && (
        <PlaylistModal
          eventId={eventId}
          venueId={state.venueId}
          venueCurrency={state.venueCurrency}
          playlist={state.playlist}
          title={state.modalTitle}
          showAddToLibraryCheckbox={!state.playlist}
          handleConfirm={state.handleConfirm}
          handleClose={handleClose}
          eventDate={state.eventDate}
          onVibyJingleDownload={handleVibyJingleDownload}
        />
      )}
      {state.delete && (
        <DeleteModal
          isPlaylist={true}
          name={state.deleteName}
          handleConfirm={state.handleDeleteConfirm}
          handleClose={handleDeleteClose}
        />
      )}
    </Box>
  );
}
